import React, { useEffect, useMemo, useState } from 'react';
import { BarChart3, Building2, Calendar, ClipboardList, CreditCard, DollarSign, FlaskRound, Home, Layers3, LayoutDashboard, ListChecks, LogOut, Menu, Package, UserCheck, Users, X, CheckCircle2, AlertCircle } from 'lucide-react';
import { Button } from '../../components/ui/button';
import axios from 'axios';
import StaffLogin from './StaffLogin';
import AppointmentModule from './AppointmentModule';
import AccountModule from './AccountModule';
import WantedListModule from './WantedListModule';
import DailyTaskModule from './DailyTaskModule';
import StaffManagement from './StaffManagement';
import ManagementDashboard from './ManagementDashboard';
import HealthCardModule from './HealthCardModule';
import LabTestsModule from './LabTestsModule';
import HealthProfilesModule from './HealthProfilesModule';
import CatalogDashboard from './CatalogDashboard';
import AttendanceModule from './AttendanceModule';
import HomeCollectionModule from './HomeCollectionModule';
import CustomStaffModule from './CustomStaffModule';
import B2BTracker from '../B2BTracker';
import { hasModuleAccess, getRoleLabel } from './staffAccessConfig';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MODULE_ICONS = {
  dashboard: LayoutDashboard,
  appointments: Calendar,
  accounts: DollarSign,
  wanted: ListChecks,
  daily_tasks: ClipboardList,
  healthcard: CreditCard,
  attendance: UserCheck,
  home_collection: Home,
  b2b: Building2,
  lab_tests: FlaskRound,
  health_profiles: Package,
  catalog_stats: BarChart3,
  staff_mgmt: Users,
};

const TaskLogPopup = ({ user, onClose }) => {
  const [taskLog, setTaskLog] = useState([]);
  const [loading, setLoading] = useState(true);
  const month = new Date().toISOString().slice(0, 7);

  useEffect(() => {
    const fetch = async () => {
      try {
        const endpoint = user.role === 'admin'
          ? `${API}/staff/management/monthly-task-log?month=${month}`
          : `${API}/staff/daily-tasks/all-progress?date=${new Date().toISOString().split('T')[0]}`;
        const { data } = await axios.get(endpoint, { withCredentials: true });
        setTaskLog(data);
      } catch {
        setTaskLog([]);
      }
      setLoading(false);
    };
    fetch();
  }, [user.role, month]);

  const displayRoles = [
    { key: 'nurse', label: 'Nurse' },
    { key: 'receptionist', label: 'Reception' },
    { key: 'labtech', label: 'Lab Tech' },
    { key: 'pharmacist', label: 'Pharmacist' },
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" data-testid="task-log-popup">
      <div className="max-h-[85vh] w-full max-w-3xl overflow-y-auto rounded-2xl bg-white shadow-2xl">
        <div className="sticky top-0 rounded-t-2xl bg-gradient-to-r from-[#0A2540] to-[#0EA5E9] p-5 text-white">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>{user.role === 'admin' ? 'Monthly Task Log' : 'Today\'s Team Progress'}</h2>
              <p className="mt-1 text-sm text-white/70">{user.role === 'admin' ? new Date().toLocaleDateString('en-IN', { month: 'long', year: 'numeric' }) : new Date().toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</p>
            </div>
            <button onClick={onClose} className="rounded-full bg-white/15 p-2 transition-colors hover:bg-white/25" data-testid="task-log-popup-x"><X className="h-5 w-5" /></button>
          </div>
        </div>

        <div className="p-5">
          {loading ? (
            <div className="flex items-center justify-center py-8"><div className="h-8 w-8 animate-spin rounded-full border-b-2 border-[#0A2540]" /></div>
          ) : user.role === 'admin' ? (
            taskLog.length === 0 ? <p className="py-8 text-center text-[#94A3B8]">No task data for this month yet</p> : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-[#F1F5F9]"><tr><th className="px-3 py-2 text-left font-semibold text-[#0A2540]">Date</th>{displayRoles.map((role) => <th key={role.key} className="px-3 py-2 text-center font-semibold text-[#0A2540]">{role.label}</th>)}</tr></thead>
                  <tbody className="divide-y divide-[#F1F5F9]">
                    {taskLog.slice(-10).reverse().map((row) => (
                      <tr key={row.date} className="hover:bg-[#F8FAFC]">
                        <td className="whitespace-nowrap px-3 py-2 font-medium text-[#0A2540]">{row.date}</td>
                        {displayRoles.map((role) => {
                          const data = row.roles?.[role.key];
                          if (!data) return <td key={role.key} className="px-3 py-2 text-center text-[#CBD5E1]">-</td>;
                          const pct = data.total > 0 ? Math.round((data.completed / data.total) * 100) : 0;
                          return <td key={role.key} className="px-3 py-2"><div className="flex items-center justify-center gap-2"><div className="h-2.5 w-14 overflow-hidden rounded-full bg-[#E2E8F0]"><div className={`h-2.5 rounded-full ${pct === 100 ? 'bg-green-500' : pct >= 50 ? 'bg-amber-400' : 'bg-red-400'}`} style={{ width: `${pct}%` }} /></div><span className={`text-xs font-bold ${pct === 100 ? 'text-green-600' : pct >= 50 ? 'text-amber-600' : 'text-red-500'}`}>{data.completed}/{data.total}</span></div></td>;
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )
          ) : taskLog.length === 0 ? (
            <div className="py-8 text-center"><AlertCircle className="mx-auto mb-3 h-12 w-12 text-amber-400" /><p className="mb-1 text-lg font-semibold text-[#0A2540]">No tasks logged today</p><p className="text-sm text-[#94A3B8]">Be the first to update your daily tasks.</p></div>
          ) : (
            <div className="space-y-3">
              {taskLog.map((progress) => {
                const pct = progress.total > 0 ? Math.round((progress.completed / progress.total) * 100) : 0;
                const isYou = progress.role === user.role;
                return (
                  <div key={`${progress.role}-${progress.name}`} className={`rounded-xl p-4 ${isYou ? 'bg-[#0A2540] text-white' : 'bg-[#F8FAFC]'}`}>
                    <div className="mb-2 flex items-center justify-between">
                      <div className="flex items-center gap-2"><span className={`rounded-full px-2 py-0.5 text-xs font-medium capitalize ${isYou ? 'bg-white/20 text-white' : 'bg-slate-100 text-slate-700'}`}>{progress.role}</span><span className={`text-sm font-medium ${isYou ? 'text-white' : 'text-[#0A2540]'}`}>{progress.name} {isYou ? '(You)' : ''}</span></div>
                      <div className="flex items-center gap-1">{pct === 100 ? <CheckCircle2 className={`h-4 w-4 ${isYou ? 'text-green-300' : 'text-green-500'}`} /> : null}<span className={`text-sm font-bold ${pct === 100 ? (isYou ? 'text-green-300' : 'text-green-600') : pct >= 50 ? (isYou ? 'text-amber-300' : 'text-amber-600') : (isYou ? 'text-red-300' : 'text-red-500')}`}>{pct}%</span></div>
                    </div>
                    <div className={`h-2.5 w-full rounded-full ${isYou ? 'bg-white/20' : 'bg-[#E2E8F0]'}`}><div className={`h-2.5 rounded-full ${pct === 100 ? 'bg-green-500' : pct >= 50 ? 'bg-amber-400' : 'bg-red-400'}`} style={{ width: `${pct}%` }} /></div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="border-t border-[#E2E8F0] p-4"><Button onClick={onClose} className="w-full bg-[#0A2540]" data-testid="task-log-popup-close">Continue to portal</Button></div>
      </div>
    </div>
  );
};

const StaffPortal = () => {
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);
  const [activeModule, setActiveModule] = useState('dashboard');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showTaskLog, setShowTaskLog] = useState(false);
  const [config, setConfig] = useState({ roles: [], modules: [] });

  const loadPortal = async () => {
    try {
      const authRes = await axios.get(`${API}/auth/me`, { withCredentials: true });
      const configRes = await axios.get(`${API}/staff/config`, { withCredentials: true });
      setUser(authRes.data);
      setConfig(configRes.data);
    } catch {
      setUser(null);
      setConfig({ roles: [], modules: [] });
    }
    setChecking(false);
  };

  useEffect(() => { loadPortal(); }, []);

  const navigationModules = useMemo(() => {
    const dynamicModules = (config.modules || []).map((module) => ({
      id: module.key,
      label: module.label,
      icon: MODULE_ICONS[module.key] || Layers3,
      adminOnly: Boolean(module.admin_only),
      module,
    }));

    return dynamicModules.filter((module, index, arr) => arr.findIndex((item) => item.id === module.id) === index);
  }, [config.modules]);

  const visibleModules = useMemo(() => navigationModules.filter((module) => {
    if (!user) return false;
    if (module.adminOnly) return user.role === 'admin';
    return hasModuleAccess(user, module.id);
  }), [navigationModules, user]);

  useEffect(() => {
    if (!user || visibleModules.length === 0) return;
    if (!visibleModules.some((module) => module.id === activeModule)) {
      setActiveModule(visibleModules[0].id);
    }
  }, [activeModule, user, visibleModules]);

  const handleLogin = async (userData) => {
    setUser(userData);
    setShowTaskLog(userData.role !== 'b2b');
    try {
      const configRes = await axios.get(`${API}/staff/config`, { withCredentials: true });
      setConfig(configRes.data);
    } catch {
      setConfig({ roles: [], modules: [] });
    }
  };

  const handleLogout = async () => {
    await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    setUser(null);
    setConfig({ roles: [], modules: [] });
    setShowTaskLog(false);
  };

  const activeModuleConfig = navigationModules.find((module) => module.id === activeModule);

  const renderModule = () => {
    switch (activeModule) {
      case 'dashboard': return user?.role === 'admin' ? <ManagementDashboard user={user} /> : null;
      case 'appointments': return <AppointmentModule user={user} />;
      case 'accounts': return <AccountModule user={user} />;
      case 'wanted': return <WantedListModule user={user} />;
      case 'daily_tasks': return <DailyTaskModule user={user} />;
      case 'healthcard': return <HealthCardModule user={user} />;
      case 'attendance': return <AttendanceModule user={user} />;
      case 'home_collection': return <HomeCollectionModule user={user} />;
      case 'b2b': return <B2BTracker embeddedUser={user} embeddedMode />;
      case 'lab_tests': return user?.role === 'admin' ? <LabTestsModule user={user} /> : null;
      case 'health_profiles': return user?.role === 'admin' ? <HealthProfilesModule user={user} /> : null;
      case 'catalog_stats': return user?.role === 'admin' ? <CatalogDashboard user={user} /> : null;
      case 'staff_mgmt': return user?.role === 'admin' ? <StaffManagement user={user} /> : null;
      default: return activeModuleConfig ? <CustomStaffModule module={activeModuleConfig.module} /> : null;
    }
  };

  if (checking) return <div className="flex min-h-screen items-center justify-center bg-[#0A2540]" data-testid="staff-portal-loading"><div className="h-12 w-12 animate-spin rounded-full border-b-2 border-white" /></div>;
  if (!user) return <StaffLogin onLogin={handleLogin} />;

  return (
    <div className="flex min-h-screen bg-[#F1F5F9]" data-testid="staff-portal-shell">
      {showTaskLog && user.role !== 'b2b' && <TaskLogPopup user={user} onClose={() => setShowTaskLog(false)} />}

      <aside className="hidden w-72 flex-col bg-[#0A2540] text-white lg:flex" data-testid="staff-sidebar-desktop">
        <div className="border-b border-white/10 p-4">
          <div className="flex items-center space-x-3">
            <img src="https://customer-assets.emergentagent.com/job_amc-care/artifacts/rt3uhs8c_AW_name%20board_curve%202X4.pdf%20%284%29.png" alt="AMC" className="h-10 w-10" />
            <div><p className="text-sm font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>AMC Staff Portal</p><p className="text-xs text-sky-300">{getRoleLabel(user.role, config.roles)}</p></div>
          </div>
        </div>
        <nav className="flex-1 space-y-1 p-3">
          {visibleModules.map((module) => {
            const Icon = module.icon;
            return <button key={module.id} onClick={() => setActiveModule(module.id)} className={`flex w-full items-center space-x-3 rounded-xl px-4 py-3 text-sm font-medium transition-colors ${activeModule === module.id ? 'bg-white/15 text-white' : 'text-slate-300 hover:bg-white/5'}`} data-testid={`sidebar-${module.id}`}><Icon className="h-5 w-5" /><span>{module.label}</span></button>;
          })}
        </nav>
        <div className="border-t border-white/10 p-3"><div className="mb-2 px-4 py-2 text-xs text-slate-400">{user.name} ({user.email})</div><button onClick={handleLogout} className="flex w-full items-center space-x-3 rounded-xl px-4 py-3 text-sm text-slate-300 transition-colors hover:bg-white/5" data-testid="staff-logout"><LogOut className="h-5 w-5" /><span>Logout</span></button></div>
      </aside>

      {sidebarOpen && (
        <div className="fixed inset-0 z-50 lg:hidden" data-testid="staff-sidebar-mobile-overlay">
          <div className="absolute inset-0 bg-black/50" onClick={() => setSidebarOpen(false)} />
          <aside className="absolute bottom-0 left-0 top-0 flex w-72 flex-col bg-[#0A2540] text-white">
            <div className="flex items-center justify-between border-b border-white/10 p-4">
              <div className="flex items-center space-x-3"><img src="https://customer-assets.emergentagent.com/job_amc-care/artifacts/rt3uhs8c_AW_name%20board_curve%202X4.pdf%20%284%29.png" alt="AMC" className="h-10 w-10" /><div><p className="text-sm font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>AMC Staff Portal</p><p className="text-xs text-sky-300">{getRoleLabel(user.role, config.roles)}</p></div></div>
              <button onClick={() => setSidebarOpen(false)} data-testid="staff-mobile-close"><X className="h-5 w-5" /></button>
            </div>
            <nav className="flex-1 space-y-1 p-3">
              {visibleModules.map((module) => {
                const Icon = module.icon;
                return <button key={module.id} onClick={() => { setActiveModule(module.id); setSidebarOpen(false); }} className={`flex w-full items-center space-x-3 rounded-xl px-4 py-3 text-sm font-medium transition-colors ${activeModule === module.id ? 'bg-white/15 text-white' : 'text-slate-300 hover:bg-white/5'}`} data-testid={`mobile-sidebar-${module.id}`}><Icon className="h-5 w-5" /><span>{module.label}</span></button>;
              })}
            </nav>
            <div className="border-t border-white/10 p-3"><div className="mb-2 px-4 py-2 text-xs text-slate-400">{user.name}</div><button onClick={handleLogout} className="flex w-full items-center space-x-3 rounded-xl px-4 py-3 text-sm text-slate-300 transition-colors hover:bg-white/5" data-testid="staff-mobile-logout"><LogOut className="h-5 w-5" /><span>Logout</span></button></div>
          </aside>
        </div>
      )}

      <div className="flex min-h-screen min-w-0 flex-1 flex-col">
        <header className="flex items-center justify-between bg-white px-4 py-3 shadow-sm lg:hidden" data-testid="staff-mobile-header">
          <button onClick={() => setSidebarOpen(true)} data-testid="staff-mobile-menu"><Menu className="h-6 w-6 text-[#0A2540]" /></button>
          <h1 className="text-sm font-bold text-[#0A2540]" style={{ fontFamily: 'Outfit, sans-serif' }}>{visibleModules.find((module) => module.id === activeModule)?.label || 'Staff Portal'}</h1>
          <button onClick={handleLogout} className="text-[#475569]" data-testid="staff-mobile-header-logout"><LogOut className="h-5 w-5" /></button>
        </header>
        <main className="min-w-0 flex-1 overflow-auto p-4 lg:p-6" data-testid="staff-main-content">{renderModule()}</main>
      </div>
    </div>
  );
};

export default StaffPortal;